import React, { useState } from "react";
import { api } from "@/lib/api";
import { TID } from "@/constants/tids";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ReportToolbar from "@/components/analyst/ReportToolbar";
import { Sparkles, Loader2, FileText } from "lucide-react";

const REMARK_PLUGINS = [remarkGfm];

export default function AnalystReport({
  initialReport,
  initialGeneratedAt,
  onReportGenerated,
  onReportCleared,
}) {
  const [report, setReport] = useState(initialReport || null);
  const [generatedAt, setGeneratedAt] = useState(initialGeneratedAt || null);
  const [loading, setLoading] = useState(false);
  // Inline markdown editor state
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      await api.generateReport();
      const start = Date.now();
      const poll = async () => {
        const st = await api.reportStatus();
        if (st.status === "done") {
          setReport(st.report_md);
          setGeneratedAt(st.generated_at);
          setLoading(false);
          onReportGenerated?.(st.report_md, st.generated_at);
          toast.success("Analysis complete.");
          return;
        }
        if (st.status === "error") {
          setLoading(false);
          toast.error(st.error || "Report generation failed.");
          return;
        }
        if (Date.now() - start > 180000) {
          setLoading(false);
          toast.error("Analysis timed out. Please try again.");
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setLoading(false);
      toast.error(e.response?.data?.detail || "Report generation failed.");
    }
  };

  const startEdit = () => {
    setDraft(report || "");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft("");
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await api.saveReport(draft);
      setReport(res.report_md);
      setGeneratedAt(res.generated_at);
      setEditing(false);
      onReportGenerated?.(res.report_md, res.generated_at);
      toast.success("Report saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save report.");
    } finally {
      setSaving(false);
    }
  };

  const clearReport = async () => {
    setClearing(true);
    try {
      await api.clearReport();
      setReport(null);
      setGeneratedAt(null);
      setEditing(false);
      setDraft("");
      onReportCleared?.();
      toast.success("Report cleared.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clear report.");
    } finally {
      setClearing(false);
    }
  };

  if (!report && !editing) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mb-5">
          <Sparkles className="w-8 h-8 text-primary-foreground" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">AI Sales Analysis</h2>
        <p className="mt-2 text-muted-foreground max-w-md">
          Generate an executive-grade written analysis of your pipeline health, win/loss,
          rep performance, deal risk, forecast and prioritized recommendations — powered by
          Claude Sonnet 4.5. Any Q&amp;A you flagged in “Query Data” will be woven in.
        </p>
        <Button
          data-testid={TID.generateReportBtn}
          onClick={generate}
          disabled={loading}
          className="mt-6"
          size="lg"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing your CRM data…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" /> Generate Analysis
            </>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <FileText className="w-4 h-4" />
          {generatedAt && `Generated ${new Date(generatedAt).toLocaleString()}`}
        </div>
        <ReportToolbar
          editing={editing}
          saving={saving}
          loading={loading}
          clearing={clearing}
          onSave={saveEdit}
          onCancel={cancelEdit}
          onEdit={startEdit}
          onRegenerate={generate}
          onClear={clearReport}
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          {editing ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Edit the report in Markdown. Changes are saved to the server and used by the
                formatted view, PDF export, and Print.
              </p>
              <Textarea
                data-testid={TID.reportEditor}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="font-mono text-sm min-h-[520px] leading-relaxed"
                spellCheck={false}
              />
            </div>
          ) : (
            <article
              data-testid={TID.reportContent}
              className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-semibold prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-3 prose-strong:text-foreground prose-table:text-sm"
            >
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{report}</ReactMarkdown>
            </article>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
